import { motion } from "framer-motion";
import { MANIFESTO } from "@/constants/testIds";

const CHAPTERS = [
  {
    n: "01",
    title: "Strategy",
    body:
      "We start every engagement the way boardrooms do — with a sharp read of the business problem, the market, and what actually moves the needle. No frameworks for their own sake.",
  },
  {
    n: "02",
    title: "Tech",
    body:
      "We build with modern, agentic systems and solid engineering — not slideware. If it can be automated, reasoned about, or shipped as software, we build it ourselves.",
  },
  {
    n: "03",
    title: "Product",
    body:
      "Strategy and tech mean nothing until someone uses the thing. We design and ship products people actually adopt, measured by outcomes, not deliverables.",
  },
];

const IMAGE_URL =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_agentic-labs-1/artifacts/fvdch12g_image.png";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

export default function Manifesto() {
  return (
    <section
      id="manifesto"
      data-testid={MANIFESTO.section}
      className="relative py-24 md:py-36 px-6 md:px-10 max-w-7xl mx-auto"
    >
      <div className="grid md:grid-cols-12 gap-10 md:gap-6">
        <motion.div
          data-testid={MANIFESTO.intro}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeUp}
          className="md:col-span-5"
        >
          <span className="text-xs tracking-[0.25em] uppercase font-medium text-brand">
            The Manifesto
          </span>
          <h2 className="font-display text-3xl md:text-5xl leading-tight tracking-tight text-ink mt-4">
            One team.
            <br />
            Three disciplines.
            <br />
            <span className="text-muted-foreground">Zero silos.</span>
          </h2>
          <p className="mt-6 text-base leading-relaxed text-muted-foreground max-w-sm">
            We call it{" "}
            <span className="text-ink font-medium">3 in a box</span> — strategy,
            technology and product held by the same set of hands on every
            engagement, not split across departments.
          </p>

          <div className="relative mt-10 max-w-sm">
            <div
              className="absolute -inset-4 rounded-2xl bg-gradient-to-br from-brand/30 via-brand-indigo/20 to-brand-teal/30 blur-2xl"
              aria-hidden="true"
            />
            <div className="relative rounded-xl overflow-hidden border border-border/70 shadow-sm">
              <img
                src={IMAGE_URL}
                alt="Strategy and ideation in motion"
                className="w-full h-56 object-cover"
              />
            </div>
          </div>
        </motion.div>

        <div className="md:col-span-7 flex flex-col divide-y divide-border/60">
          {CHAPTERS.map((c, i) => (
            <motion.div
              key={c.n}
              data-testid={MANIFESTO.chapter(c.n)}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              transition={{ delay: i * 0.1 }}
              className="grid grid-cols-[64px_1fr] md:grid-cols-[96px_1fr] gap-4 md:gap-8 py-8 md:py-10 first:pt-0 last:pb-0"
            >
              <span className="font-display text-4xl md:text-5xl text-border font-light tracking-tighter">
                {c.n}
              </span>
              <div>
                <h3 className="font-display text-xl md:text-2xl tracking-tight text-ink mb-2">
                  {c.title}
                </h3>
                <p className="text-base leading-relaxed text-muted-foreground max-w-md">
                  {c.body}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
